// useTenantUsers — fetches /tenants/{tenantId}/users with the current
// filters, exposes refetch(). Mirrors useUsers structurally; scoped to
// one tenant.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ListTenantUsersQuery,
  ListTenantUsersResponse,
  TenantId,
} from '@gc-coach/shared';
import { listTenantUsers } from '../api/endpoints.js';

export interface UseTenantUsersResult {
  data: ListTenantUsersResponse | null;
  isLoading: boolean;
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useTenantUsers(
  tenantId: TenantId,
  query: ListTenantUsersQuery,
): UseTenantUsersResult {
  const [data, setData] = useState<ListTenantUsersResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const key = useMemo(
    () => `${tenantId}|${query.status ?? ''}|${query.q?.trim() ?? ''}`,
    [tenantId, query.status, query.q],
  );

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    const result = await listTenantUsers(tenantId, query);
    if (result.ok) {
      setData(result.data);
      setErrorCode(null);
    } else {
      setErrorCode(result.code);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  return { data, isLoading, errorCode, refetch: fetchNow };
}
